import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { IconAlertCircle, IconBrandGit, IconFile, IconFlask, IconFolder } from '@tabler/icons-react';
import { api, LORE_PREFIX, type OmniHit, type OmniSource } from '../api.js';
import { formatSize, relativeTime } from '../lib/format.js';

/**
 * /search?q=… — omni search results across Perforce, Lore, and git,
 * grouped by source. Backed by the file_index table; each row links to
 * the same file modal the per-source browsers use.
 */

const LABEL: Record<OmniSource, string> = { p4: 'Perforce', lore: 'Lore', git: 'Git' };
const ORDER: OmniSource[] = ['p4', 'lore', 'git'];

function dirOf(p: string): string {
  const i = p.lastIndexOf('/');
  return i >= 0 ? p.slice(0, i) : '';
}

/** Where a hit opens: the source's browser at the file's directory, with the file modal up. */
function hrefFor(h: OmniHit): { pathname: string; search: string } {
  if (h.source === 'p4') {
    return { pathname: `/depot${dirOf(h.path).replace(/^\/\//, '/')}`, search: `?file=${encodeURIComponent(h.path)}` };
  }
  if (h.source === 'lore') {
    const rel = dirOf(h.path.slice(LORE_PREFIX.length));
    return { pathname: `/lore/${rel}`, search: `?file=${encodeURIComponent(h.path)}` };
  }
  // git: <owner>/<repo>/<path>
  const segs = h.path.split('/');
  const repo = segs.slice(0, 2).join('/');
  const rel = segs.slice(2).join('/');
  const dir = dirOf(rel);
  return { pathname: dir ? `/git/${repo}/${dir}` : `/git/${repo}`, search: `?file=${encodeURIComponent(rel)}` };
}

function displayDir(h: OmniHit): string {
  if (h.source === 'lore') return dirOf(h.path.slice(LORE_PREFIX.length));
  return dirOf(h.path);
}

export default function SearchPage() {
  const location = useLocation();
  const q = useMemo(() => (new URLSearchParams(location.search).get('q') ?? '').trim(), [location.search]);
  const res = useQuery({
    queryKey: ['omni', q],
    queryFn: () => api.omniSearch(q, 300),
    enabled: q.length >= 2,
    retry: false,
    staleTime: 30_000,
  });

  if (q.length < 2) {
    return <p className="text-sm text-muted-foreground">Type at least two characters to search.</p>;
  }
  if (res.isLoading) return <Spinner />;
  if (res.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{(res.error as Error).message}</AlertDescription>
      </Alert>
    );
  }
  const results = res.data?.results ?? [];
  const total = res.data?.total ?? 0;
  const groups = ORDER.map((s) => ({ source: s, hits: results.filter((h) => h.source === s) })).filter((g) => g.hits.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        {total} match{total === 1 ? '' : 'es'} for &ldquo;{q}&rdquo;
        {results.length < total && ` — showing ${results.length}`}
      </p>
      {groups.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
      {groups.map((g) => (
        <section key={g.source}>
          <h2 className="mb-1 flex items-center gap-2 text-sm font-medium">
            {g.source === 'p4' ? <IconFolder size={15} className="text-theia-500" /> : g.source === 'lore' ? <IconFlask size={15} className="text-theia-400" /> : <IconBrandGit size={15} className="text-muted-foreground" />}
            {LABEL[g.source]} <span className="text-xs font-normal text-muted-foreground">({g.hits.length})</span>
          </h2>
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full table-fixed text-sm">
              <tbody>
                {g.hits.map((h) => (
                  <tr key={`${h.source}:${h.path}`} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                    <td className="min-w-0 px-3 py-1.5">
                      <Link to={hrefFor(h)} className="flex items-center gap-2 hover:text-theia-400 hover:underline">
                        <IconFile size={16} className="shrink-0 text-muted-foreground" />
                        <span>{h.name}</span>
                      </Link>
                      <div className="truncate pl-6 font-mono text-[11px] text-muted-foreground" title={h.path}>
                        {displayDir(h)}
                      </div>
                    </td>
                    <td className="w-24 px-3 py-1.5 text-right font-mono text-[11px] text-muted-foreground">
                      {h.change !== null ? (
                        <Link to={`/changes/${String(h.change)}`} className="hover:text-theia-400 hover:underline">@{h.change}</Link>
                      ) : h.fileSize !== null ? formatSize(h.fileSize) : ''}
                    </td>
                    <td className="w-32 whitespace-nowrap px-3 py-1.5 text-right text-[11px] text-muted-foreground">{relativeTime(h.changedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
